import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  approveTenant,
  createHostTenant,
  expireTenant,
  getHostDashboard,
  getHostTenants,
  rejectTenant,
  updateTenantLicense,
  updateHostTenant,
  resendTenantOnboarding,
  type CreateHostTenantRequest,
  type UpdateHostTenantRequest,
  type HostTenantStatus,
  type TenantLicenseRequest,
} from '../../tenants/api/tenants';

export function useHostDashboard() {
  return useQuery({
    queryKey: ['host-dashboard'],
    queryFn: getHostDashboard,
  });
}

export function useHostTenants(status?: HostTenantStatus) {
  return useQuery({
    queryKey: ['host-tenants', status ?? 'all'],
    queryFn: () => getHostTenants(status),
  });
}

export function useApproveHostTenant() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ tenantId, license }: { tenantId: string; license: TenantLicenseRequest }) =>
      approveTenant(tenantId, license),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['host-tenants'] }),
        queryClient.invalidateQueries({ queryKey: ['host-dashboard'] }),
      ]);
    },
  });
}

export function useRejectHostTenant() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: rejectTenant,
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['host-tenants'] }),
        queryClient.invalidateQueries({ queryKey: ['host-dashboard'] }),
      ]);
    },
  });
}

export function useExpireHostTenant() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: expireTenant,
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['host-tenants'] }),
        queryClient.invalidateQueries({ queryKey: ['host-dashboard'] }),
      ]);
    },
  });
}

export function useUpdateTenantLicense(onSuccess?: () => void) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ tenantId, licenseEndsAtUtc }: { tenantId: string; licenseEndsAtUtc: string }) =>
      updateTenantLicense(tenantId, licenseEndsAtUtc),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['host-tenants'] }),
        queryClient.invalidateQueries({ queryKey: ['host-dashboard'] }),
      ]);
      onSuccess?.();
    },
  });
}

export function useUpdateHostTenant(onSuccess?: () => void) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ tenantId, request }: { tenantId: string; request: UpdateHostTenantRequest }) => {
      const result = await updateHostTenant(tenantId, request);
      if (!result.success) {
        throw new Error(result.error);
      }
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['host-tenants'] }),
        queryClient.invalidateQueries({ queryKey: ['host-dashboard'] }),
      ]);
      onSuccess?.();
    },
  });
}

export function useResendTenantOnboarding() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: resendTenantOnboarding,
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['host-tenants'] }),
        queryClient.invalidateQueries({ queryKey: ['host-dashboard'] }),
      ]);
    },
  });
}

export function useCreateHostTenant(onSuccess?: () => void) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: CreateHostTenantRequest) => {
      const result = await createHostTenant(request);
      if (!result.success) {
        throw new Error(result.error);
      }

      return result.id;
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['host-tenants'] }),
        queryClient.invalidateQueries({ queryKey: ['host-dashboard'] }),
      ]);
      onSuccess?.();
    },
  });
}
